import React, { useState, useEffect } from 'react';
import apiClient from '../../apiClient';
import { FiEdit, FiTrash2, FiCopy, FiPlus } from 'react-icons/fi';
import Table from '../common/Table/Table';
import '../common/Table/Table.css';

const Source = ({ sourceDetails, setMessage }) => {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [currentSource, setCurrentSource] = useState(null);
  const [formData, setFormData] = useState({ source: '' });

  useEffect(() => {
    setSources(sourceDetails);
    setLoading(false);
  }, [sourceDetails]);

  useEffect(() => {
    fetchSources();
  }, []);

  const fetchSources = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get('/api/source');
      setSources(response.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (source) => {
    setCurrentSource(source);
    setFormData({ source: source.source });
    setShowForm(true);
  };

  const handleDeleteClick = async (id) => {
    if (!window.confirm('Are you sure you want to delete this source?')) return;
    try {
      await apiClient.delete(`/api/source/${id}`);
      setMessage({ text: 'source deleted successfully', type: 'success' });
      fetchSources();
    } catch (err) {
      setMessage({ text: err.response?.data?.error || 'Failed to delete source', type: 'error' });
    }
  };

  const handleCopyClick = (source) => {
    navigator.clipboard.writeText(source.source);
    setMessage({ text: 'source copied to clipboard', type: 'success' });
  };

  const handleFormChange = (e) => {
    setFormData({ source: e.target.value });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.source.trim()) {
      setMessage({ text: 'source cannot be empty', type: 'error' });
      return;
    }
    try {
      if (currentSource) {
        await apiClient.put(`/api/source/${currentSource.id}`, { source: formData.source });
        setMessage({ text: 'source updated successfully', type: 'success' });
      } else {
        await apiClient.post('/api/source', { source: formData.source });
        setMessage({ text: 'source added successfully', type: 'success' });
      }
      setShowForm(false);
      setCurrentSource(null);
      setFormData({ source: '' });
      fetchSources();
    } catch (err) {
      setMessage({ text: err.response?.data?.error || 'Failed to save source', type: 'error' });
    }
  };

  const handleAddNew = () => {
    setCurrentSource(null);
    setFormData({ source: '' });
    setShowForm(true);
  };

  if (loading) return <div className="loading">Loading Sources...</div>;
  if (error) return <div className="error">Error: {error}</div>;

  const columns = [
    { header: 'ID' },
    { header: 'Source' },
    { header: 'Created At' },
    { header: 'Updated At' },
    { header: 'Actions', className: 'actions-header' }
  ];

  const renderRow = (source) => (
    <>
      <td>{source.id}</td>
      <td>{source.source}</td>
      <td>{new Date(source.created_at).toLocaleString()}</td>
      <td>{new Date(source.updated_at).toLocaleString()}</td>
      <td className="actions-cell">
        <button
          onClick={() => handleEditClick(source)}
          className="lead-edit-btn"
          title="Edit source"
        >
          <FiEdit />
        </button>
        <button
          onClick={() => handleCopyClick(source)}
          className="copy-btn"
          title="Copy source"
        >
          <FiCopy />
        </button>
        <button
          onClick={() => handleDeleteClick(source.id)}
          className="delete-btn"
          title="Delete source"
        >
          <FiTrash2 />
        </button>
      </td>
    </>
  );

  return (
    <div className="package-tab">
      <div className="package-header">
        <h2>Source Management</h2>
        <button onClick={handleAddNew} className="add-btn">
          <FiPlus /> Add New Source
        </button>
      </div>

      {showForm && (
        <form className="modal-form" onSubmit={handleFormSubmit}>
          <div className="form-group">
            <label>Source:</label>
            <input
              type="text"
              value={formData.source}
              onChange={handleFormChange}
              autoFocus
            />
          </div>
          <div className="modal-buttons">
            <button type="submit" className="save-button">
              {currentSource ? 'Update' : 'Add'}
            </button>
            <button
              type="button"
              className="cancel-button"
              onClick={() => {
                setShowForm(false);
                setCurrentSource(null);
                setFormData({ source: '' });
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <Table 
        columns={columns} 
        data={sources} 
        renderRow={renderRow} 
        className="package-table"
      />
    </div>
  );
};

export default Source;
